import moment from "moment-timezone";

import Strings from "../../../Strings";
import { removeNonErrors } from "../../../utils/validators/rpmPatient";
import { MAX_KIT_NAME_LENGTH } from "../../CreateKitAndAddDevices/CreateKitAndAddDevices";
import { DATE_TIME_SPACE_12_UPPERCASE } from "../../../constants";

export const shipmentStatusEnum = {
  new: 'new',
  created: 'created',
  in_transit: 'in_transit',
  delivered: 'delivered',
  cancelled: 'cancelled',
};

// A date-only field (ship date, ETA) is stored as an instant anchored at
// 12:00 in the portal timezone of whoever entered it. Noon gives the calendar
// date ~12h of display margin in both directions, so every viewer within that
// offset of the enterer reads the same date; the DatePicker's raw midnight
// value has zero westward margin — one timezone west already renders the
// previous day.
export const toNoonAnchor = val => val.clone().hour(12).minute(0).second(0).millisecond(0);

// dsApi clears the ETA when it receives Go's zero time; omitting the field
// means "leave the stored value in place".
export const ETA_CLEAR_SENTINEL = '0001-01-01T00:00:00Z';

export const shipmentFieldsEnum = {
  kit_ids: {
    fieldId: 'kit_ids',
    required: (_, status) => { return status === shipmentStatusEnum.in_transit },
  },
  patient_uuid: {
    fieldId: 'patient_uuid',
    required: () => { return true },
  },
  enrollment_uuids: {
    fieldId: 'enrollment_uuids',
    required: () => { return true },
  },
  contact_details: {
    fieldId: 'contact_details',
    required: () => { return false },
  },
  shipping_address: {
    fieldId: 'shipping_address',
    required: (_, status) => { return status === shipmentStatusEnum.in_transit },
  },
  shipping_method: {
    fieldId: 'shipping_method',
    required: (_, status) => { return status === shipmentStatusEnum.in_transit },
  },
  tracking_number: {
    fieldId: 'tracking_number',
    required: (_, status) => { return status === shipmentStatusEnum.in_transit },
  },
  provider_order_no: {
    fieldId: 'provider_order_no',
    required: () => { return false },
  },
  sales_order_no: {
    fieldId: 'sales_order_no',
    required: () => { return false },
  },
  eta: {
    fieldId: 'eta',
    required: () => { return false },
    toDTO: val => (val && moment.isMoment(val)) ? toNoonAnchor(val).format() : val,
  },
  ship_date: {
    fieldId: 'ship_date',
    required: () => { return false },
    toDTO: val => (val && moment.isMoment(val)) ? toNoonAnchor(val).format() : val,
  },
  delivered_date: {
    fieldId: 'delivered_date',
    required: (_, status) => { return status === shipmentStatusEnum.delivered },
    toDTO: val => (val && moment.isMoment(val)) ? val.format() : val,
  },
  shipping_cost: {
    fieldId: 'shipping_cost',
    toDTO: val => val === '' ? 0 : val,
    acceptNull: true,
    required: () => { return false },
  },
  order_uuid: {
    fieldId: 'order_uuid',
     required: () => { return true },
  },
  return_method: {
    fieldId: 'return_method',
     required: () => { return false },
  },
};

// Serializes the form state per shipmentFieldsEnum. With `context` it also
// strips date fields the current interaction cannot legitimately change:
// dates act only in their mode, an unchanged ship date is not a correction,
// and while a live SmartMeter order manages the shipment the vendor owns all
// three dates.
export const shipmentToDTO = (shipment, context) => {
  const dto = Object.values(shipmentFieldsEnum).reduce((acc, field) => {
    const value = shipment[field.fieldId];
    if (value || field.acceptNull) {
      acc[field.fieldId] = field.toDTO ? field.toDTO(value, shipment) : value;
    }
    return acc;
  }, {});
  if (!context) {
    return dto;
  }

  const { mode, status, recordedShipDate, originalEta, isSmartMeterManaged } = context;
  if (mode !== 'delivered') {
    delete dto.delivered_date;
  }
  const shipDateApplies = mode === 'ship' || mode === 'return'
    || (mode === 'edit' && status === shipmentStatusEnum.in_transit);
  if (!shipDateApplies) {
    delete dto.ship_date;
  }
  // "Shipped today" is not a backdate: omit the field so the backend stamps
  // the real click instant — the value every downstream consumer of this
  // timestamp (billing, reports, filters, device timeline) expects.
  if ((mode === 'ship' || mode === 'return') && dto.ship_date
    && moment(dto.ship_date).isSame(moment(), 'day')) {
    delete dto.ship_date;
  }
  // An unchanged date is not a correction. Compare displayed dates, not
  // instants: the recorded value may be a click instant or a noon anchor,
  // and what the user saw in the form is its calendar date.
  if (mode === 'edit' && dto.ship_date && recordedShipDate
    && moment(dto.ship_date).isSame(moment(recordedShipDate), 'day')) {
    delete dto.ship_date;
  }
  // Correcting to today: its noon may still be ahead of the clock; cap at
  // now so the backend's future tolerance is never tripped.
  if (dto.ship_date && moment(dto.ship_date).isAfter(moment())) {
    dto.ship_date = moment().format();
  }
  // ETA is three-state against the stored value: unchanged displayed date →
  // omit (dsApi keeps what it has, and an editor in another timezone must
  // not silently re-anchor the instant), cleared → the explicit clear
  // sentinel (omission would keep the old value), changed → the noon anchor
  // already in the dto.
  if (originalEta) {
    if (dto.eta && moment(dto.eta).isSame(moment(originalEta), 'day')) {
      delete dto.eta;
    } else if (!dto.eta) {
      dto.eta = ETA_CLEAR_SENTINEL;
    }
  }
  if (isSmartMeterManaged) {
    delete dto.ship_date;
    delete dto.delivered_date;
    delete dto.eta;
  }
  return dto;
};

export const validateShipment = (shipment, status, kitRequired) => {
  const errors = validateKit(shipment.kit_ids, shipment, status, kitRequired)
    .concat(validateEnrollmentUUID(shipment.enrollment_uuids, shipment, status))
    .concat(validateShippingAddress(shipment.shipping_address, shipment, status))
    .concat(validateShippingMethod(shipment.shipping_method, shipment, status))
    .concat(validateTrackingNumber(shipment.tracking_number, shipment, status))
    .concat(validateEta(shipment.eta, shipment, status))
    .concat(validateShipDate(shipment.ship_date, shipment, status))
    .concat(validateDeliveredDate(shipment.delivered_date, shipment, status));
  return errors;
};

const validateKit = (kits, shipment, status, kitRequired) => {
  const ret = [];
  if (!kits?.length && kitRequired) {
    ret.push({ property: 'kit_ids', propertyName: Strings.kitId, errors: [], missing: shipmentFieldsEnum.kit_ids.required(shipment, status) });
  } else if (kits?.length > MAX_KIT_NAME_LENGTH) {
    ret.push({ property: 'kit_ids', propertyName: Strings.kitId, errors: [Strings.formatString(Strings.capPatient.wrongFieldLengthFormat, MAX_KIT_NAME_LENGTH)]});
  }
  return removeNonErrors(ret);
};

const validateEnrollmentUUID = (enrollmentUUID, shipment, status) => {
  if (!shipment.patient_uuid) {
    return [];
  }
  const ret = [];
  if (!enrollmentUUID || !enrollmentUUID.length) {
    ret.push({ property: 'enrollment_uuid', propertyName: Strings.enrollment, errors: [], missing: shipmentFieldsEnum.enrollment_uuids.required(shipment, status) });
  }
  return removeNonErrors(ret);
};

const validateShippingAddress = (shippingAddress, shipment, status) => {
  const ret = [];
  if (!shippingAddress || !shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip || !shippingAddress.country) {
    ret.push({ property: 'shipping_address', propertyName: Strings.shipping.shippingAddress, errors: [], missing: shipmentFieldsEnum.shipping_address.required(shipment, status) });
  }
  return removeNonErrors(ret);
}

const validateShippingMethod = (shippingMethod, shipment, status) => {
  const ret = [];
  if (!shippingMethod) {
    ret.push({ property: 'shipping_method', propertyName: Strings.shipping.shippingMethod, errors: [], missing: shipmentFieldsEnum.shipping_method.required(shipment, status) });
  }
  return removeNonErrors(ret);
}

const validateTrackingNumber = (trackingNumber, shipment, status) => {
  const ret = [];
  if (!trackingNumber) {
    ret.push({ property: 'tracking_number', propertyName: Strings.shipping.trackingNumber, errors: [], missing: shipmentFieldsEnum.tracking_number.required(shipment, status) });
  }
  return removeNonErrors(ret);
}

// Late data entry legitimately produces ETAs and delivered dates before the
// shipment record even existed, so neither field validates against created_at.
const validateEta = (eta, shipment, status) => {
  const ret = {property: 'eta', propertyName: Strings.shipping.eta, errors: []};
  if (!eta) {
    ret.missing = shipmentFieldsEnum.eta.required(shipment, status);
  }
  if (eta && !eta.isValid()) {
    ret.errors.push(Strings.capPatient.incorrectFieldFormat);
  }
  return removeNonErrors([ret]);
}

export const validateShipDate = (shipDate, shipment, status) => {
  const ret = { property: 'ship_date', propertyName: Strings.shipping.shippingDate, errors: [] };
  if (!shipDate) {
    ret.missing = shipmentFieldsEnum.ship_date.required(shipment, status);
  } else if (!shipDate.isValid()) {
    ret.errors.push(Strings.capPatient.incorrectFieldFormat);
  } else if (shipDate.isAfter(moment(), 'day')) {
    ret.errors.push(Strings.errors.dateInTheFuture);
  }
  return removeNonErrors([ret]);
}

export const validateDeliveredDate = (deliveredDate, shipment, status) => {
  const ret = { property: 'delivered_date', propertyName: Strings.shipping.deliveryDate, errors: [] };
  if (!deliveredDate) {
    ret.missing = shipmentFieldsEnum.delivered_date.required(shipment, status);
  } else {
    // The entered ship date is the meaningful lower bound: the in-transit
    // history row carries it (possibly backdated).
    const inTransitDate = shipment.status_history?.find(history => history.status === shipmentStatusEnum.in_transit)?.created_at;
    if (!deliveredDate.isValid()) {
      ret.errors.push(Strings.capPatient.incorrectFieldFormat);
    } else if (deliveredDate.isAfter(moment().endOf('day'))) {
      ret.errors.push(Strings.errors.dateInTheFuture);
    }
    else if (inTransitDate && deliveredDate.isBefore(moment(inTransitDate))) {
      ret.errors.push(Strings.formatString(Strings.shipping.deliveryDateBeforeInTransit, moment(inTransitDate).format(DATE_TIME_SPACE_12_UPPERCASE)));
    }
  }
  return removeNonErrors([ret]);
}
